import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { FiMail, FiUser, FiPhone, FiLock, FiAlertCircle, FiChevronRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { Button, Input } from './ui';
import AuthSidePanel from './AuthSidePanel';

const RegisterStepOne = ({ onNext }) => {
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', phone: '', email: '', password: '', password_confirm: '',
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        if (formData.password !== formData.password_confirm) {
            setErrors({ password_confirm: 'Passwords do not match' });
            return;
        }
        setIsLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/users/register-step-one/`, formData, {
                withCredentials: true,
            });
            if (response.status === 200) {
                if (response.data.user_id) {
                    localStorage.setItem('tempUserId', response.data.user_id);
                    onNext(response.data.user_id);
                } else {
                    setErrors({ general: 'Failed to get user ID from server' });
                }
            }
        } catch (error) {
            if (error.response) setErrors(error.response.data);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="min-h-screen grid lg:grid-cols-2 bg-white dark:bg-gray-900">
            {/* Form (left on desktop, top on mobile) */}
            <div className="order-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-lg">
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Create your account</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                            Join EventSawa and start connecting with amazing events and services.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Input name="first_name" label="First name" placeholder="Jane"
                                leftIcon={<FiUser className="w-5 h-5" />} value={formData.first_name}
                                onChange={handleChange} error={errors.first_name} required />
                            <Input name="last_name" label="Last name" placeholder="Doe"
                                leftIcon={<FiUser className="w-5 h-5" />} value={formData.last_name}
                                onChange={handleChange} error={errors.last_name} required />
                            <Input name="email" type="email" label="Email address" placeholder="you@example.com"
                                leftIcon={<FiMail className="w-5 h-5" />} value={formData.email}
                                onChange={handleChange} error={errors.email} required />
                            <Input name="phone" type="tel" label="Phone number" placeholder="+254 7XX XXX XXX"
                                leftIcon={<FiPhone className="w-5 h-5" />} value={formData.phone}
                                onChange={handleChange} error={errors.phone} required />
                            <Input name="password" type={showPassword ? 'text' : 'password'} label="Password"
                                placeholder="Create a password" leftIcon={<FiLock className="w-5 h-5" />}
                                rightIcon={
                                    <button type="button" onClick={() => setShowPassword((s) => !s)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                        {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                    </button>
                                }
                                value={formData.password} onChange={handleChange} error={errors.password} required />
                            <Input name="password_confirm" type={showConfirm ? 'text' : 'password'} label="Confirm password"
                                placeholder="Repeat password" leftIcon={<FiLock className="w-5 h-5" />}
                                rightIcon={
                                    <button type="button" onClick={() => setShowConfirm((s) => !s)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                        {showConfirm ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                    </button>
                                }
                                value={formData.password_confirm} onChange={handleChange}
                                error={errors.password_confirm} required />
                        </div>

                        {(errors.general || errors.non_field_errors) && (
                            <div className="flex items-center p-3 text-sm text-red-700 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400">
                                <FiAlertCircle className="mr-2 flex-shrink-0" />
                                {errors.general || errors.non_field_errors}
                            </div>
                        )}

                        <Button type="submit" size="lg" fullWidth loading={isLoading}>
                            {isLoading ? 'Processing…' : 'Sign up'}
                        </Button>

                        <p className="text-center text-sm text-gray-600 dark:text-gray-300">
                            Already have an account?{' '}
                            <Link to="/login" className="font-medium text-indigo-600 dark:text-indigo-400 inline-flex items-center gap-1">
                                Login here <FiChevronRight className="w-4 h-4" />
                            </Link>
                        </p>
                    </form>
                </div>
            </div>

            {/* Catchy panel (right on desktop, below on mobile) */}
            <div className="order-2">
                <AuthSidePanel />
            </div>
        </section>
    );
};

export default RegisterStepOne;
